import { Button, Flex, SimpleGrid } from "@mantine/core";
import { useCallback, useState } from "react";

import { useClikData } from "../../../api/hooks/clikData/useClikData.ts";
import { useUpdateClikCompanyData } from "../../../api/hooks/clikData/useUpdateClikCompanyData.ts";
import { useCreateCustomerDirectorInfo } from "../../../api/hooks/customer/useCreateCustomerDirectorInfo.ts";
import { useCreateRiskMonitoringRemarks } from "../../../api/hooks/customer/useCreateRiskMonitoringRemarks.ts";
import { useCustomerDirectorInfo } from "../../../api/hooks/customer/useCustomerDirectorInfo.ts";
import { useRiskMonitoringRemarks } from "../../../api/hooks/customer/useRiskMonitoringRemarks.ts";
import { useUpdateCustomerDirectorInfo } from "../../../api/hooks/customer/useUpdateCustomerDirectorInfo.ts";
import { useUpdateRiskMonitoringRemarks } from "../../../api/hooks/customer/useUpdateRiskMonitoringRemarks.ts";
import MultiFieldCard from "../../../components/Cards/MultiFieldCard/MultiFieldCard.tsx";
import { ClikDataFormOut } from "../../../components/ClikDataInfoForm/types.ts";
import SvgEdit from "../../../components/Icons/Edit.tsx";
import {
  ClikCity,
  ClikCountry,
  RolePermission,
  UpdateCustomerDirectorInfoInput,
  UpdateCustomerRiskMonitoringRemarksInput,
} from "../../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../../hooks/useMutationNotificationWrapper.tsx";
import { useCurrentUserContext } from "../../../providers/CurrentUserProvider.tsx";
import { CustomerOption, DirectorInfo } from "../../../types/customer.ts";
import { toClikPhoneFormat } from "../../../utils/phone.ts";
import { hasPermission } from "../../../utils/user.ts";
import DirectorInfoForm from "./DirectorInfoForm.tsx";
import { useClikDataCompanyInfoCards } from "./hooks/useClikDataCompanyInfoCards.tsx";
import { useCustomerDirectorInfoCard } from "./hooks/useCustomerDirectorInfoCard.tsx";
import { useEditLimitRequestCompanyInfo } from "./hooks/useEditLimitRequestCompanyInfo.tsx";
import { useRiskMonitoringCard } from "./hooks/useRiskMonitoringCard.tsx";
import RiskMonitoringRemarksForm from "./RiskMonitoringRemarksForm.tsx";
import { EditMode } from "./types.ts";
import { formatToUpdateClikDataInput } from "./utils.ts";

interface CompanyInfoProps {
  clikDataParentId: number | null | undefined;
  isFetching: boolean;
  customerDirectorInfo: DirectorInfo | null | undefined;
  cities: ClikCity[] | null | undefined;
  countries: ClikCountry[] | null | undefined;
  companies: CustomerOption[] | null | undefined;
}

const CompanyInfo = ({
  companies,
  cities,
  countries,
  clikDataParentId,
  customerDirectorInfo,
  isFetching,
}: CompanyInfoProps) => {
  const [editMode, setEditMode] = useState<EditMode | null>(null);

  const [value, setValue] = useState<ClikDataFormOut | null>(null);

  const [{ data: response, fetching: fetchingClikData }, refreshClikData] =
    useClikData({ id: clikDataParentId });
  const data = response?.clikData;
  const customerId = data?.company?.id;

  const [, refreshCustomerDirectorInfo] = useCustomerDirectorInfo({
    customerId,
  });

  const [
    {
      data: riskMonitoringRemarksData,
      fetching: fetchingRiskMonitoringRemarks,
    },
    refreshRiskMonitoringRemarks,
  ] = useRiskMonitoringRemarks({
    customerId,
  });
  const riskMonitoringRemarks =
    riskMonitoringRemarksData?.customerRiskMonitoringRemarks;

  const [, updateClikData] = useMutationNotificationWrapper(
    useUpdateClikCompanyData(),
    {
      success: {
        message: "Clik Data has been updated successfully.",
      },
    }
  );

  const [, createDirectorInfo] = useMutationNotificationWrapper(
    useCreateCustomerDirectorInfo(),
    {
      success: {
        message: "Director Info has been updated successfully.",
      },
    }
  );
  const [, updateDirectorInfo] = useMutationNotificationWrapper(
    useUpdateCustomerDirectorInfo(),
    {
      success: {
        message: "Director Info has been updated successfully.",
      },
    }
  );

  const [, createRiskMonitoringRemarks] = useMutationNotificationWrapper(
    useCreateRiskMonitoringRemarks(),
    {
      success: {
        message: "Risk Monitoring has been updated successfully.",
      },
    }
  );
  const [, updateRiskMonitoringRemarks] = useMutationNotificationWrapper(
    useUpdateRiskMonitoringRemarks(),
    {
      success: {
        message: "Risk Monitoring has been updated successfully.",
      },
    }
  );

  const { user: currentUser } = useCurrentUserContext();

  const [companyInfo, companyAddress] = useClikDataCompanyInfoCards({ data });

  const directorInfo = useCustomerDirectorInfoCard({
    data: customerDirectorInfo,
  });

  const riskMonitoringInfo = useRiskMonitoringCard({
    data: riskMonitoringRemarks,
  });

  const hasEditAccess = hasPermission(
    currentUser,
    RolePermission.CanEditBuyerInfoCompanyInfo
  );

  const handleEdit = (type: EditMode) => setEditMode(type);

  const handleCancel = () => setEditMode(null);

  const handleValueChange = (value: ClikDataFormOut) => {
    setValue(value);
  };

  const handleSave = () => {
    if (!value || !data) {
      return;
    }
    const input = formatToUpdateClikDataInput(value);
    if (!input) {
      return;
    }
    updateClikData({
      id: Number(data.id),
      input: {
        ...input,
        phone: toClikPhoneFormat(input.phone),
      },
    }).then(() => {
      refreshClikData();
      setEditMode(null);
    });
  };

  const handleEditDirectorInfo = useCallback(
    (input: UpdateCustomerDirectorInfoInput) => {
      if (!customerId) {
        return;
      }
      if (customerDirectorInfo?.id) {
        return updateDirectorInfo({
          id: customerDirectorInfo.id,
          input,
        }).then(refreshCustomerDirectorInfo);
      }
      createDirectorInfo({
        input: {
          ...input,
          customerId,
        },
      }).then(refreshCustomerDirectorInfo);
    },
    [
      customerId,
      createDirectorInfo,
      customerDirectorInfo?.id,
      refreshCustomerDirectorInfo,
      updateDirectorInfo,
    ]
  );

  const handleEditRiskMonitoringRemarks = useCallback(
    (input: UpdateCustomerRiskMonitoringRemarksInput) => {
      if (!customerId) {
        return;
      }
      const handler = riskMonitoringRemarks?.id
        ? updateRiskMonitoringRemarks({
            id: riskMonitoringRemarks.id,
            input,
          })
        : createRiskMonitoringRemarks({
            input: {
              ...input,
              customerId,
            },
          });
      handler.then(refreshRiskMonitoringRemarks);
    },
    [
      riskMonitoringRemarks?.id,
      updateRiskMonitoringRemarks,
      createRiskMonitoringRemarks,
      customerId,
      refreshRiskMonitoringRemarks,
    ]
  );

  const editLimitRequestCompanyInfo = useEditLimitRequestCompanyInfo({
    data,
    cities,
    companies,
    countries,
    onSave: handleSave,
    onCancel: handleCancel,
    onChange: handleValueChange,
  });

  if (editMode === EditMode.CompanyInfo) {
    return (
      <Flex direction="column" gap={32}>
        <Flex direction="column" gap={20}>
          {editLimitRequestCompanyInfo}
        </Flex>
      </Flex>
    );
  }

  if (editMode === EditMode.DirectorInCharge) {
    return (
      <Flex direction="column" gap={32}>
        <Flex direction="column" gap={20}>
          <DirectorInfoForm
            value={customerDirectorInfo}
            onCancel={handleCancel}
            onSubmit={handleEditDirectorInfo}
          />
        </Flex>
      </Flex>
    );
  }

  if (editMode === EditMode.RiskMonitoringRemarks) {
    return (
      <Flex direction="column" gap={32}>
        <Flex direction="column" gap={20}>
          <RiskMonitoringRemarksForm
            value={riskMonitoringRemarks}
            onCancel={handleCancel}
            onSubmit={handleEditRiskMonitoringRemarks}
          />
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap={32}>
      <Flex direction="column" gap={20}>
        {hasEditAccess && (
          <Button
            variant="filledGolden"
            leftIcon={<SvgEdit />}
            uppercase
            loading={isFetching || fetchingClikData}
            onClick={() => handleEdit(EditMode.CompanyInfo)}
          >
            edit
          </Button>
        )}
        <SimpleGrid
          cols={3}
          breakpoints={[
            { minWidth: "sm", cols: 2 },
            { minWidth: "lg", cols: 3 },
          ]}
          spacing={20}
          verticalSpacing={20}
        >
          <MultiFieldCard
            label="Company Info"
            variant="blue"
            loading={isFetching || fetchingClikData}
            items={companyInfo}
          />
          <MultiFieldCard
            label="Company Address"
            variant="blue"
            loading={isFetching || fetchingClikData}
            items={companyAddress}
          />
        </SimpleGrid>
        <SimpleGrid
          cols={1}
          breakpoints={[
            { minWidth: "sm", cols: 2 },
            { minWidth: "lg", cols: 3 },
          ]}
          spacing={20}
          verticalSpacing={20}
        >
          <Flex direction="column" gap={20}>
            {hasEditAccess && (
              <Button
                variant="filledGolden"
                leftIcon={<SvgEdit />}
                uppercase
                loading={isFetching}
                onClick={() => handleEdit(EditMode.DirectorInCharge)}
              >
                edit
              </Button>
            )}
            <MultiFieldCard
              label="Director In Charge Info"
              variant="blue"
              loading={isFetching}
              items={directorInfo}
            />
          </Flex>
          <Flex direction="column" gap={20}>
            {hasEditAccess && (
              <Button
                variant="filledGolden"
                leftIcon={<SvgEdit />}
                uppercase
                loading={isFetching || fetchingRiskMonitoringRemarks}
                onClick={() => handleEdit(EditMode.RiskMonitoringRemarks)}
              >
                edit
              </Button>
            )}
            <MultiFieldCard
              label="Risk Monitoring"
              variant="blue"
              loading={isFetching || fetchingRiskMonitoringRemarks}
              items={riskMonitoringInfo}
            />
          </Flex>
        </SimpleGrid>
      </Flex>
    </Flex>
  );
};

export default CompanyInfo;
